import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { Checkbox } from '@interfaces/a-organizar/checkbox';
import { Option } from '@interfaces/common/option';
import { dfpHistoricalRequest } from '@interfaces/dfp/request/dfpIncomeStatementRequest';
import { dfpHistoricalBalanceSheetResponse } from '@interfaces/dfp/response/dfpHistoricalBalanceSheetResponse';
import { dfpHistoricalHighlightsResponse } from '@interfaces/dfp/response/dfpHistoricalHighlightsResponse';
import { dfpHistoricalIncomeStatementResponse } from '@interfaces/dfp/response/dfpIncomeStatementResponse';
import { dfpHistoricalRevenueStatementResponse } from '@interfaces/dfp/response/dfpRevenueStatementResponse';
import {
  dfpSourceDocumentResponse,
  dfpSourceDocumentsResponse,
} from '@interfaces/dfp/response/dfpSourceDocumentsResponse';
import { hasReviewedStatements } from './utils/dfp-status.util';
import { statementPeriodResponse } from '@interfaces/dfp/response/statementPeriodResponse';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { TourBuilder, TourService, TourStep } from '@services/helpers/tour.service';
import { UtilsService } from '@services/helpers/utils.service';
import { CompanyService } from '@services/http/company.service';
import { DFPService } from '@services/http/dfp/dfp.service';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { SharedModule } from '@shared/shared.module';
import { TableBalanceSheetComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/table-balance-sheet/table-balance-sheet.component';
import { TableIncomedStatementComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/table-incomed-statement/table-incomed-statement.component';
import { TableRevenueStatementComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/table-revenue-statement/table-revenue-statement.component';
import { AppState } from '@src/app/state';
import { selectCompanyId } from '@src/app/state/auth/auth.selectors';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { combineLatest, finalize, first, of, Subject, take, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { PermissionsService } from '@services/helpers/permissions.service';
import { FloatingAiButtonComponent } from '@shared/components/floating-ai-button/floating-ai-button.component';
import { SourceDocumentsPanelComponent } from './components/source-documents-panel/source-documents-panel.component';
import { StatementComparisonComponent } from './components/statement-comparison/statement-comparison.component';
import { DfpEmptyStateComponent } from './components/dfp-empty-state/dfp-empty-state.component';
import { DfpLoadErrorComponent } from './components/dfp-load-error/dfp-load-error.component';
import { DfpExportService } from './services/dfp-export.service';
import { TableHistoricalHighlightsComponent } from './components/table-historical-highlights/table-historical-highlights.component';
import {
  buildDfpColumns,
  DfpColumn,
  dfpColumnKey,
  DfpPeriodAwareRow,
} from '@models/dfp/dfp-column.model';
import { DFP_VALUE_SCALE_OPTIONS, DfpValueScale } from './models/dfp-value-scale.model';
import { DfpStandardizationNavigationService } from './services/dfp-standardization-navigation.service';
import { DfpSubAccountsStore } from './services/dfp-sub-accounts.store';
import { describeSourceRemoval, userFacingBackendMessage } from './utils/dfp-screen-messages.util';
import { ProcessingDocumentsNoticeComponent } from './components/processing-documents-notice/processing-documents-notice.component';
import { CreditScoreStripComponent } from './components/credit-score-strip/credit-score-strip.component';
import {
  AccountingDocumentDetailModalComponent,
  AccountingDocumentModalRequest,
  AccountingDocumentModalResult,
} from '@src/app/features/dashboard/accounting-data-standardization/components/document-detail-modal/document-detail-modal.component';
import { RealtimeListRefreshService } from '@services/realtime/realtime-list-refresh.service';
import { PartnerIncomeTaxComponent } from './components/partner-income-tax/partner-income-tax.component';
import {
  PARTNER_INCOME_TAX_QUERY_PARAM,
  PartnerIncomeTaxStateService,
} from './components/partner-income-tax/partner-income-tax-state.service';
import { DfpTabsState } from './services/dfp-tabs.state';
import { DfpCashFlowStore } from './services/dfp-cash-flow.store';
import { TableCashFlowComponent } from './components/table-cash-flow/table-cash-flow.component';
import { DfpModulesState } from './services/dfp-modules.state';
import { StatementResizerComponent } from './components/statement-resizer/statement-resizer.component';
import { DfpModulesEmptyStateComponent } from './components/module-toggles/modules-empty-state.component';
import { StatementMasonryDirective } from './components/statement-masonry/statement-masonry.directive';
import { DfpModuleTogglesComponent } from './components/module-toggles/module-toggles.component';
import { DfpFinancialReadingComponent } from './components/financial-reading/financial-reading.component';
import { DfpStatementsDeferral } from './services/dfp-statements-deferral.service';
import { applyHiddenYearsToSourceDocuments } from './utils/dfp-hidden-years.util';

/** As três tabelas de período (DRE, Balanço, Destaques) têm seleção de coluna independente. Faturamento fica fora. */
export type DfpPeriodTable = 'income' | 'balance' | 'highlights';

@Component({
  selector: 'app-dfp',
  templateUrl: './dfp.component.html',
  styleUrls: ['./dfp.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    MenuModule,
    TableIncomedStatementComponent,
    TableBalanceSheetComponent,
    TableRevenueStatementComponent,
    TableHistoricalHighlightsComponent,
    FloatingAiButtonComponent,
    SourceDocumentsPanelComponent,
    StatementComparisonComponent,
    DfpEmptyStateComponent,
    DfpLoadErrorComponent,
    ProcessingDocumentsNoticeComponent,
    CreditScoreStripComponent,
    // Só usado dentro de `@defer` no template: a modal (e a instância própria do state service da
    // Padronização) só nasce no primeiro "Ver De/Para" — quem nunca abre não paga por ela.
    AccountingDocumentDetailModalComponent,
    PartnerIncomeTaxComponent,
    DfpModuleTogglesComponent,
    TableCashFlowComponent,
    DfpFinancialReadingComponent,
    StatementResizerComponent,
    StatementMasonryDirective,
    DfpModulesEmptyStateComponent,
  ],
  // Abas (card 3515), módulos da aba Demonstrativos (card 3801) e o estado da aba IR dos sócios: providos aqui porque
  // o contador da aba precisa do número de sócios antes de a aba ser aberta. Ver `services/` e o README da feature.
  providers: [
    DfpTabsState,
    DfpModulesState,
    DfpStatementsDeferral,
    PartnerIncomeTaxStateService,
    RealtimeListRefreshService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class DfpComponent
  extends VaultBaseComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  /**
   * Empresa fixada por quem embute a tela (card 3268: aba "Análise econômica" na etapa Gates do deal).
   * Quando vem preenchida, vence a URL e o store — é a empresa do deal, não a selecionada no topo.
   * Na rota (`/economic-analysis/financial-statement`) fica `null` e nada muda.
   */
  readonly companyId = input<string | null>(null);

  /**
   * Modo embutido, no padrão do `OnboardingV2Component` (aba Documentos do deal): sem título, sem seletor
   * de empresa, sem tour e sem gravar `?companyId` na URL — a URL é a do deal, não desta tela.
   */
  readonly embedded = input(false);

  /**
   * Somente leitura, para o PARCEIRO na aba Análise econômica do deal (card 3307, T03): as tabelas e as
   * colunas de período continuam; saem exportar, documentos de origem, comentários e o chat de IA. A tela
   * também deixa de exigir `DFP_VIEW` (o parceiro não tem; quem autoriza é o endpoint do deal) e não lê o
   * `/dfp/status` nem a lista de empresas, que são do time interno. O "Ver De/Para" some sozinho: depende
   * dos documentos de origem e de permissão que o parceiro não tem.
   */
  readonly readOnly = input(false);

  /** `DFP_VIEW`, a guarda da tela fora do modo somente leitura. */
  protected readonly canViewDfp = toSignal(
    inject(PermissionsService).hasPermission(Permissions.Dfp.VIEW),
    { initialValue: false }
  );

  /** `CREDITSCORE_VIEW_COMPANY` (card 3766): o bloco do score de crédito no topo da tela. */
  protected readonly canViewCreditScore = toSignal(
    inject(PermissionsService).hasPermission(Permissions.CreditScore.VIEW_COMPANY),
    { initialValue: false }
  );

  private readonly destroy$ = new Subject<void>();
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly exportService = inject(DfpExportService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly standardizationNavigation = inject(DfpStandardizationNavigationService);
  /** Subcontas "dentre elas" de DRE e Balanço (PBI 3423), repassadas às tabelas pelo template. */
  protected readonly subAccounts = inject(DfpSubAccountsStore);
  /** DFC dos anos realizados, na aba Demonstrativos (PBI 3875). */
  protected readonly cashFlow = inject(DfpCashFlowStore);
  /** Abas Demonstrativos | IR dos sócios, com `?tab=` (cards 3515 e 3801). */
  protected readonly tabs = inject(DfpTabsState);
  /** Barra "Mostrar" da aba Demonstrativos: quais dos quatro módulos aparecem e com que largura (card 3801). */
  protected readonly modules = inject(DfpModulesState);
  private readonly partnerIncomeTax = inject(PartnerIncomeTaxStateService);

  /**
   * Último pedido de "Ver De/Para" (menu da coluna de ano). Além de alimentar a modal, é a condição do `@defer`
   * que a cria: `null` até o primeiro clique, então nada da modal é instanciado antes de ser usado.
   */
  deParaModalRequest: AccountingDocumentModalRequest | null = null;

  hasTableData: boolean = false;
  hasDemands: boolean = true;

  /** A carga dos demonstrativos FALHOU (erro HTTP/rede): mostra `app-dfp-load-error`, nunca o estado vazio. */
  loadFailure: { message: string | null } | null = null;

  /**
   * Empresa selecionada não tem NADA para exibir: nem DRE, nem Balanço, nem Destaques, nem Faturamento.
   * Decidido pelo dado que chegou, não pelo `/dfp/status` — ver o `error` de `handleLoadPage`.
   */
  hasNoValidatedData: boolean = false;

  companiesOptions: Option[] = [];

  incomeStatementTableData: dfpHistoricalIncomeStatementResponse[] = [];
  balanceSheetTableData: dfpHistoricalBalanceSheetResponse[] = [];
  highlightsTableData: dfpHistoricalHighlightsResponse[] = [];
  revenueStatementTableData: dfpHistoricalRevenueStatementResponse[] = [];

  /** Períodos existentes nos documentos da empresa, oferecidos como opções de coluna. */
  statementPeriods: statementPeriodResponse[] = [];

  /** Origem dos números exibidos; nulo enquanto não carregou. Alimenta o painel e o menu de coluna. */
  sourceDocuments: dfpSourceDocumentsResponse | null = null;

  /**
   * Última resposta crua do backend (antes do filtro de `applyHiddenYears`). Precisa ficar guardada à parte
   * de `sourceDocuments`: `applyHiddenYears` só sabe DEMOVER um documento de `inUse` para `notUsed`, nunca
   * promover de volta — reprocessar em cima do próprio `sourceDocuments` já filtrado tornaria `onYearRestore`
   * incapaz de reverter uma ocultação anterior (o documento ficaria preso em `notUsed` para sempre). Por
   * isso todo reprocessamento local parte sempre desta cópia crua, não do resultado já filtrado.
   */
  private rawSourceDocuments: dfpSourceDocumentsResponse | null = null;

  /**
   * Colunas de período adicionadas pelo usuário nesta sessão (não são persistidas). A seleção é
   * independente por tabela: adicionar/remover uma coluna no DRE não afeta Balanço nem Destaques.
   */
  drePeriodColumns: DfpColumn[] = [];
  balancePeriodColumns: DfpColumn[] = [];
  highlightsPeriodColumns: DfpColumn[] = [];

  /**
   * Anos ocultados pelo menu do rótulo de coluna de ano (`app-year-column-menu`), efêmero e por tabela — o
   * mesmo espírito de `drePeriodColumns`/`balancePeriodColumns`/`highlightsPeriodColumns` acima, mas para
   * ocultar em vez de adicionar. Ordenado crescente sempre (ver `hideYear`), porque tanto o menu de ano
   * quanto o "+" listam a seção "Anos ocultos" na mesma ordem das colunas.
   */
  dreHiddenYears: number[] = [];
  balanceHiddenYears: number[] = [];
  highlightsHiddenYears: number[] = [];
  analysisColumns: Checkbox[] = [
    {
      label: 'AV (Análise vertical)',
      value: 'av',
      isChecked: false,
      name: 'analysisColumns',
      isDisabled: false,
    },
  ];
  chooseAnalysisColumns: FormGroup = this.formBuilder.group({
    analysisColumns: this.formBuilder.array(this.analysisColumns.map(item => item.isChecked)),
  });

  /** Canal do seletor de empresa: `app-select-company` só aceita `[formGroup]`+`[controlName]` (ver README). */
  filtersForm: FormGroup = this.formBuilder.group({
    companyId: [null as string | null],
  });

  /** Opções do seletor de escala, na barra de filtros. */
  readonly valueScaleOptions = DFP_VALUE_SCALE_OPTIONS;

  /**
   * Escala de exibição dos valores monetários, repassada às quatro tabelas.
   *
   * Estado de VISUALIZAÇÃO puro, como `showAnalysisColumns`: vive só no componente, não vai para a URL nem
   * para o `localStorage`, e volta ao padrão a cada entrada na tela. Trocar de escala não recarrega nada — os
   * dados já estão em memória e só a formatação muda.
   */
  valueScale: DfpValueScale = 'original';

  selectedCompanyId: string | null = null;

  /**
   * A lista de empresas já voltou. Até lá, sem empresa resolvida, a tela mostra o carregando, e não "selecione uma
   * empresa" (a empresa da URL ou do store pode estar chegando).
   */
  companiesLoaded = false;

  /** Demonstrativos adiados quando a tela abre na aba IR dos sócios (card 3515, G2; ver `DfpStatementsDeferral`). */
  private readonly statementsDeferral = inject(DfpStatementsDeferral);
  protected readonly statementsPending = this.statementsDeferral.pending;

  showAnalysisColumns: { [key: string]: boolean } = {
    av: false,
  };
  exportMenuItems: MenuItem[] = [];

  private readonly initialTourSteps: TourStep[] = [
    {
      id: 'companyId',
      text: this.t(this.tk.General.SelectCompanyToStart),
      element: '.company-select',
      position: 'top',
      buttonText: 'Ok',
    },
  ];

  public initialTour: TourService = this.tourBuilder.build(this.initialTourSteps, {
    persistent: true,
    storageKey: 'dfp-tour',
  });

  constructor(
    translateService: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly utilsService: UtilsService,
    private readonly companyService: CompanyService,
    private formBuilder: FormBuilder,
    private readonly toast: ToastHelperService,
    private readonly dfpService: DFPService,
    private readonly cdr: ChangeDetectorRef,
    protected override store: Store<AppState>,
    private tourBuilder: TourBuilder,
    private router: Router
  ) {
    super(translateService);
    // Empresa e escala entram como FUNÇÃO, não valor: o menu é montado uma vez aqui e as duas mudam depois
    // — a empresa no `app-select-company`, a escala no seletor da barra de filtros. Cada item passa pela guarda
    // da exportação: ela lê as tabelas do DOM e elas podem ainda não ter sido carregadas (G2).
    this.exportMenuItems = this.exportService
      .buildMenuItems(
        () => this.getSelectedCompanyName(),
        () => this.valueScale
      )
      .map(item => ({
        ...item,
        command: event =>
          this.statementsDeferral.guardExport(this.isLoading, () => item.command?.(event)),
      }));
    this.statementsDeferral.connect(() => this.loadStatements());
  }

  ngOnInit() {
    // IR dos sócios nunca no modo somente leitura (parceiro); a permissão própria o state service confere.
    this.partnerIncomeTax.setEnabled(!this.readOnly());
    // `?partner=` só na rota: embutida, a URL é do deal.
    this.partnerIncomeTax.initUrlSync(!this.embedded());
    this.tabs.init(this.embedded());

    // Empresa fixada por quem embute > URL > store. Resolvida JÁ, sem esperar a lista de empresas (card 3515, G1):
    // antes a tela mostrava "selecione uma empresa" e o IR dos sócios só era pedido depois do `GET companys`.
    combineLatest([
      this.route.queryParams.pipe(first()),
      this.store.select(selectCompanyId).pipe(first()),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, companyIdFromStore]) => {
        const initial =
          this.companyId() || (params?.['companyId'] as string | undefined) || companyIdFromStore;
        if (initial && !this.selectedCompanyId) this.applyInitialCompany(initial);
      });

    // A lista de empresas agora só alimenta as opções (nome da empresa na exportação) e decide o tour/estado
    // "selecione uma empresa". Somente leitura (parceiro) não lista empresas: a empresa é a do deal.
    (this.readOnly() ? of(null) : this.companyService.getAllCompanys().pipe(take(1)))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: companiesResponse => {
          this.companiesOptions =
            companiesResponse?.data
              .filter(company => company.id)
              .map(company => ({
                label: company.displayName || '',
                value: company.id as string,
              })) || [];
          this.companiesLoaded = true;

          this.startCompanyTourIfNeeded();

          this.cdr.markForCheck();
        },
        error: error => {
          this.companiesLoaded = true;
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorLoadingCompanys),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
          this.setIsLoadingLocal(false);
        },
        complete: () => {
          this.setIsLoadingLocal(false);
        },
      });

    this.filtersForm
      .get('companyId')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(companyId => this.onDfpCompanySelected(companyId));

    // O menu de coluna vive nas três tabelas; a modal é UMA, aqui. O pedido chega pelo serviço.
    this.standardizationNavigation.documentModalRequests$
      .pipe(takeUntil(this.destroy$))
      .subscribe(request => {
        this.deParaModalRequest = request;
        this.cdr.markForCheck();
      });
  }

  /**
   * Empresa inicial (input, URL ou store): carrega na hora, sem esperar a lista de empresas. Sincroniza o controle
   * sem disparar `valueChanges`: é a carga inicial, não uma troca do usuário.
   */
  private applyInitialCompany(companyId: string): void {
    this.selectedCompanyId = companyId;
    this.filtersForm.get('companyId')!.setValue(companyId, { emitEvent: false });
    this.handleLoadPage();
    this.cdr.markForCheck();
  }

  /**
   * Modal "Ver De/Para" fechada. Só recarrega quando algo foi gravado lá dentro, e recarrega pelo mesmo
   * caminho de trocar coluna (`reloadDFPHistorical`): DRE, Balanço, Destaques, Faturamento e documentos de
   * origem voltam do backend com os valores novos, mas as colunas de período pedidas, os anos ocultos, a AV e
   * a escala são estado DESTE componente e não passam por `clearPageState` — a tela volta exatamente como o
   * usuário a deixou. Sem alteração, nada é pedido: recarregar à toa faria a tela piscar.
   */
  onDeParaModalClosed(result: AccountingDocumentModalResult): void {
    if (result.changed) this.reloadDFPHistorical();
  }

  /**
   * Um documento contábil entrou ou saiu do conjunto em processamento (aviso `app-dfp-processing-documents-notice`):
   * a coluna daquele ano mudou de origem. Recarrega pelo mesmo caminho da modal, preservando colunas, anos
   * ocultos, AV e escala.
   */
  onProcessingSetChanged(): void {
    this.reloadDFPHistorical();
  }

  /**
   * "Validar de novo" ou "Tirar da análise" no modal "De onde vem cada mês" (card 3700): o faturamento mudou. Recarrega
   * pelo mesmo caminho, o que traz a tabela de Faturamento nova e, pela DRE recarregada, a conferência receita x
   * faturamento. O modal fica aberto (a tabela de Faturamento não é desmontada) e relê a própria origem.
   */
  onRevenueOriginChanged(): void {
    this.reloadDFPHistorical();
  }

  /**
   * `[formGroup]`+`[controlName]` do `app-select-company` entrega o `value` (string) da opção via
   * `valueChanges`, não o objeto `Option` que o output legado `sendSelectedOption` emitia — o handler só
   * usava `.value`, então recebe o valor direto em vez de fazer lookup na lista de opções.
   */
  onDfpCompanySelected(companyId: string | null): void {
    this.selectedCompanyId = companyId ?? null;
    if (this.selectedCompanyId && this.initialTour.isActive()) this.initialTour.complete();
    this.handleCompanyChange();
  }

  /**
   * Mostra o balão "Para iniciar, selecione uma empresa" só quando, DEPOIS de resolvida a empresa inicial (entrada,
   * URL ou store), nenhuma foi escolhida. Antes (até 07/10/2026) a decisão era um `setTimeout` de 200 ms no
   * `ngAfterViewInit`, mas a empresa da URL só é aplicada quando a lista de empresas volta do backend, o que costuma
   * passar de 200 ms: o balão aparecia por cima da tela já carregada. Embutida, o seletor nem existe.
   */
  private startCompanyTourIfNeeded(): void {
    if (this.selectedCompanyId || this.embedded()) return;
    // Próximo ciclo: o `.company-select` precisa estar renderizado para o balão se ancorar nele.
    setTimeout(() => {
      if (!this.selectedCompanyId) this.initialTour.start();
    });
  }

  /**
   * Embutida, a tela pode trocar de empresa sem ser recriada (o deal muda debaixo dela). Mesmo caminho
   * da troca pelo seletor, que no modo embutido não grava a URL. A primeira carga fica com o `ngOnInit`.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['companyId'];
    if (!change || change.firstChange) return;

    const companyId = this.companyId();
    if (!companyId || companyId === this.selectedCompanyId) return;

    this.selectedCompanyId = companyId;
    this.filtersForm.get('companyId')!.setValue(companyId, { emitEvent: false });
    this.handleCompanyChange();
  }

  ngAfterViewInit() {
    // O balão de "selecione uma empresa" é decidido em `startCompanyTourIfNeeded`, depois da empresa inicial.
    this.forceExportButtonNativeTitle();
  }

  /**
   * `app-button` repassa `[title]="title"` para `p-button`, mas o `Button` do PrimeNG 21 não declara
   * `title` como `@Input` — o valor nunca chega ao `<button>` nativo (mesma causa raiz documentada em
   * `TableAbstractComponent`). `app-button` é componente compartilhado, fora de escopo mexer nele aqui;
   * por isso o atributo é aplicado direto no `<button>` renderizado.
   */
  private forceExportButtonNativeTitle(): void {
    // ⚠️ Seletor casado com a POSIÇÃO do botão no template. Ele já viveu no header e passou a viver na
    // barra de filtros; se mudar de lugar de novo, atualize aqui — a falha é silenciosa (o tooltip
    // some, nada quebra no console).
    const exportButton = (this.elementRef.nativeElement as HTMLElement).querySelector(
      '.dfp-historical__filters__export app-button button'
    );
    if (exportButton) {
      this.renderer.setAttribute(exportButton, 'title', 'Exportar');
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleCompanyChange(): void {
    this.clearPageState();
    // Embutida, a URL é de quem embute (o deal): gravar `?companyId` nela sujaria o link da outra tela.
    if (this.embedded()) {
      this.handleLoadPage();
      return;
    }
    // Atualiza a URL com o companyId. O sócio aberto na aba IR dos sócios (`?partner=`) é da empresa anterior: sai
    // na mesma navegação (duas navegações seguidas, a segunda cancelaria a primeira).
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { companyId: this.selectedCompanyId, [PARTNER_INCOME_TAX_QUERY_PARAM]: null },
      queryParamsHandling: 'merge', // mantém outros parâmetros
    });
    this.handleLoadPage();
  }

  private clearPageState() {
    this.hasTableData = false;
    // `hasDemands`/`hasNoValidatedData` são conclusões sobre a empresa ANTERIOR: sem zerar, a troca de
    // empresa mostraria por um instante o estado vazio da empresa que acabou de sair de tela.
    this.hasDemands = true;
    this.hasNoValidatedData = false;
    this.loadFailure = null;
    this.balanceSheetTableData = [];
    this.highlightsTableData = [];
    this.revenueStatementTableData = [];
    this.incomeStatementTableData = [];
    // Períodos e colunas selecionadas são por empresa: trocar de empresa zera a seleção.
    this.statementPeriods = [];
    this.sourceDocuments = null;
    this.rawSourceDocuments = null;
    this.subAccounts.clear();
    this.cashFlow.clear();
    this.drePeriodColumns = [];
    this.balancePeriodColumns = [];
    this.highlightsPeriodColumns = [];
    // Anos ocultados também são por empresa e efêmeros — mesma régua acima, mesmo motivo.
    this.dreHiddenYears = [];
    this.balanceHiddenYears = [];
    this.highlightsHiddenYears = [];
  }

  handleLoadPage() {
    // A aba IR dos sócios segue a mesma empresa; ela só pede ao backend com a permissão.
    this.partnerIncomeTax.setCompany(this.selectedCompanyId);
    if (!this.selectedCompanyId) return;

    // Abriu direto na aba IR dos sócios: os demonstrativos esperam a troca de aba (`DfpStatementsDeferral`).
    if (this.statementsDeferral.deferIfPartnerTabRequested(this.readOnly())) {
      this.cdr.markForCheck();
      return;
    }
    this.loadStatements();
  }

  /** Carga dos demonstrativos (status, períodos, as quatro tabelas, subcontas e documentos de origem). */
  private loadStatements(): void {
    this.statementsDeferral.markLoading();
    if (!this.selectedCompanyId) return;

    this.setIsLoadingLocal(true);

    const params = { companyId: this.selectedCompanyId } as dfpHistoricalRequest;

    // Somente leitura (parceiro): o `/dfp/status` é do time interno. O estado vazio continua nascendo do dado,
    // como no caminho de erro abaixo.
    if (this.readOnly()) {
      // Fecha o `true` acima: sem o `getDFPStatus`, o `finalize` que faria isso não roda (o contador do
      // loading global ficaria preso em 1 quando a empresa muda depois do init).
      this.setIsLoadingLocal(false);
      this.loadStatementPeriods(params);
      this.loadDFPHistoricalData(params);
      return;
    }

    // ⚠️ `utilsService.setIsLoading` é um CONTADOR de referência (`loadingCounter`), não um booleano: o
    // overlay global só desaparece quando o contador volta a zero, ou seja, cada `true` exige o seu `false`.
    // O `false` que fecha o `true` da linha acima precisa rodar em QUALQUER desfecho da chamada de status —
    // e o `complete` do `subscribe` não roda quando o observable falha. Enquanto esse `false` viveu no
    // `complete`, empresa sem dados (que chega pelo `error`) deixava o contador preso em 1 e a tela ficava
    // com loading infinito por cima da mensagem. `finalize` cobre sucesso, erro e unsubscribe.
    this.dfpService
      .getDFPStatus(params)
      .pipe(finalize(() => this.setIsLoadingLocal(false)))
      .subscribe({
        next: response => {
          const statusDRE = response;
          if (!statusDRE) {
            this.hasDemands = false;
            return;
          }

          this.hasDemands = hasReviewedStatements(statusDRE);

          if (this.hasDemands) {
            // Períodos disponíveis são por empresa, não por recorte de coluna: carregar UMA vez aqui, e não
            // dentro de loadDFPHistoricalData. Adicionar/remover uma coluna de período não muda os períodos
            // que existem nos documentos da empresa, então recarregá-los a cada troca seria uma requisição
            // desnecessária sem nenhum dado novo para mostrar.
            this.loadStatementPeriods(params);
            this.loadDFPHistoricalData(params);
          }

          this.cdr.markForCheck();
        },
        // Empresa sem anexo de Balanço/DRE NÃO chega aqui como flags em `false`: o `/dfp/status` responde
        // com ERRO. As seis flags do DTO são hardcoded `true` no backend (a validação real está comentada
        // em `DfpService.GetDfpStatus`), e é o `DfpValidator` que adiciona
        // `BalanceSheetAttachmentNotFound`/`IncomeStatementAttachmentNotFound` — o `ResultObject` volta 200
        // com `status: false`, e o `VaultApiService` transforma isso em erro. Antes desta mudança o usuário
        // recebia um toast de falha (com o rótulo de OUTRA feature, "operações Comex") e a área de conteúdo
        // ficava em branco.
        //
        // Não dá para distinguir "sem dados" de falha real pela mensagem: os resources são localizados
        // (en-US/es-ES/pt-BR), então casar string quebraria conforme o idioma do usuário. Por isso quem
        // decide é o DADO: seguimos para os endpoints históricos e o estado vazio nasce de "nenhuma das
        // quatro tabelas veio com linha" (ver `processDFPHistoricalData`). Falha técnica de verdade continua
        // aparecendo, só que vinda da carga dos dados, que é onde ela importa.
        error: () => {
          this.loadStatementPeriods(params);
          this.loadDFPHistoricalData(params);
        },
      });
  }

  setIsLoadingLocal(isLoading: boolean): void {
    this.utilsService.setIsLoading(isLoading);
    this.isLoading = isLoading;
    this.cdr.detectChanges();
  }

  private loadDFPHistoricalData(params: dfpHistoricalRequest): void {
    this.loadFailure = null;
    this.setIsLoadingLocal(true);
    this.dfpService
      .getDFPHistoricalData(params, {
        income: this.drePeriodColumns,
        balance: this.balancePeriodColumns,
        highlights: this.highlightsPeriodColumns,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.processDFPHistoricalData(data);
          this.loadSourceDocuments(params);
          this.cdr.markForCheck();
          this.setIsLoadingLocal(false);
        },
        error: error => this.handleError(error),
      });
  }

  /**
   * Carrega os documentos de origem para o recorte atual de colunas. Falha aqui não derruba a tela: sem a
   * lista, a Análise Econômica continua exibindo os números — o painel apenas não aparece.
   */
  private loadSourceDocuments(params: dfpHistoricalRequest): void {
    // Documentos de origem são do time interno (e o endpoint do deal não os expõe).
    if (this.readOnly()) return;

    this.cashFlow.load(params);
    this.subAccounts.load(params, {
      income: this.drePeriodColumns,
      balance: this.balancePeriodColumns,
    });
    this.dfpService
      .getDFPHistoricalSourceDocuments(params, {
        income: this.drePeriodColumns,
        balance: this.balancePeriodColumns,
        highlights: this.highlightsPeriodColumns,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: documents => {
          this.rawSourceDocuments = documents;
          this.sourceDocuments = this.applyHiddenYears(documents);
          this.cdr.markForCheck();
        },
        error: () => {
          this.rawSourceDocuments = null;
          this.sourceDocuments = null;
          this.cdr.markForCheck();
        },
      });
  }

  /** Documentos de origem sem os anos ocultos nesta sessão (regra em `utils/dfp-hidden-years.util.ts`). */
  private applyHiddenYears(
    documents: dfpSourceDocumentsResponse | null
  ): dfpSourceDocumentsResponse | null {
    return applyHiddenYearsToSourceDocuments(documents, {
      incomeHiddenYears: this.dreHiddenYears,
      balanceHiddenYears: this.balanceHiddenYears,
      incomePeriodColumns: this.drePeriodColumns,
      balancePeriodColumns: this.balancePeriodColumns,
    });
  }

  /**
   * Carrega os períodos que existem nos documentos da empresa, para alimentar o seletor de colunas.
   * Falha aqui não derruba a tela: sem períodos, a Análise Econômica segue exibindo só as colunas anuais.
   */
  private loadStatementPeriods(params: dfpHistoricalRequest): void {
    this.dfpService
      .getStatementPeriods(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: periods => {
          this.statementPeriods = periods ?? [];
          this.cdr.markForCheck();
        },
        error: () => {
          this.statementPeriods = [];
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Períodos que o "+" daquela tabela ainda pode oferecer: sem ano inteiro, sem os que já estão sendo
   * EXIBIDOS como coluna naquela tabela, e (para DRE/Balanço) restritos aos períodos que de fato existem
   * naquele demonstrativo.
   *
   * "Exibido" não é só "está nos dados" (`buildDfpColumns(this.tableDataFor(table))`) — é dados **E**
   * pedido (`periodColumnsFor(table)`), a mesma regra que `TableAbstractComponent.buildVisibleColumns` usa
   * para decidir o que de fato aparece na tabela. É a interseção dos dois, não o estado pedido isolado, por
   * um motivo real de produção (empresa STG_MEDIFARMA): o backend pode devolver, junto do período pedido,
   * uma coluna de período que ninguém pediu — o balancete de dezembro carrega a comparativa de dezembro do
   * ano anterior (pedir só `2024 · dez` no Balanço traz `2024 · dez` **e** `2023 · dez`). Essa coluna extra
   * não pode aparecer na tabela (`TableAbstractComponent` já a filtra), mas TAMBÉM não pode ficar marcada
   * como "já é coluna" aqui, senão o "+" para de oferecê-la e o usuário perde a chance de adicionar
   * `2023 · dez` de propósito.
   *
   * E não pode ser só o estado pedido isolado (sem cruzar com os dados) pelo motivo oposto, já coberto por
   * teste antes desta correção: o backend pode não ter dados daquele demonstrativo no período pedido (ex.:
   * Balanço sem linhas de jul–dez mesmo com o período adicionado), e nesse caso a coluna nunca nasce — se o
   * filtro olhasse só o estado pedido, o período sumiria do "+" sem nenhuma coluna com "×" para desfazer,
   * ficando irremovível.
   *
   * Ver README ("Por que `availablePeriodsFor` filtra pelos dados INTERSECCIONADOS com o pedido") para a
   * tabela completa dos três casos.
   */
  availablePeriodsFor(table: DfpPeriodTable): statementPeriodResponse[] {
    const requestedKeys = new Set(this.periodColumnsFor(table).map(column => column.key));
    const displayedKeys = new Set(
      buildDfpColumns(this.tableDataFor(table))
        .map(column => column.key)
        .filter(key => requestedKeys.has(key))
    );
    // `dfpColumnKey` é a mesma função usada por `buildDfpColumns` acima para montar a chave "ano:início-fim"
    // — reaproveitar em vez de remontar a string à mão evita ter duas regras de formação de chave. Só é
    // seguro porque o filtro de `isFullYear` já garantiu periodStartMonth/periodEndMonth não nulos.
    return this.statementPeriods
      .filter(period => !period.isFullYear)
      .filter(period => !displayedKeys.has(dfpColumnKey(period)))
      .filter(period => this.matchesStatementFor(table, period));
  }

  /**
   * Restringe os períodos oferecidos ao demonstrativo da tabela: DRE só oferece `hasIncomeStatement` e
   * Balanço só `hasBalanceSheet` — evita que o "+" de um demonstrativo ofereça um período que só existe no
   * outro (o usuário escolhia e nada acontecia, porque não havia dado daquele demonstrativo naquele período).
   * Destaques NÃO filtra por demonstrativo — decisão adiada do dono do produto: a tabela de Destaques cruza
   * DRE e Balanço (ex.: Dívida Líquida/EBITDA), e a regra correta para ela (exigir os dois? só um?) ainda não
   * foi definida. Não "consertar" isso adicionando o filtro aqui sem alinhar com produto antes.
   */
  private matchesStatementFor(table: DfpPeriodTable, period: statementPeriodResponse): boolean {
    if (table === 'income') return period.hasIncomeStatement;
    if (table === 'balance') return period.hasBalanceSheet;
    return true;
  }

  /**
   * Adiciona a coluna na tabela pedida e recarrega — só aquele endpoint muda de recorte. Ignora se a `key`
   * já estiver no estado daquela tabela: como um período sem dados continua oferecido no "+" (ver
   * `availablePeriodsFor`), sem essa guarda um segundo clique empilharia a mesma coluna duas vezes.
   */
  onPeriodAdd(table: DfpPeriodTable, column: DfpColumn): void {
    const current = this.periodColumnsFor(table);
    if (current.some(existing => existing.key === column.key)) return;

    this.setPeriodColumns(table, [...current, column]);
    this.reloadDFPHistorical();
  }

  /** Remove a coluna da tabela pedida e recarrega. */
  onPeriodRemove(table: DfpPeriodTable, key: string): void {
    this.setPeriodColumns(
      table,
      this.periodColumnsFor(table).filter(column => column.key !== key)
    );
    this.reloadDFPHistorical();
  }

  /**
   * "Substituir por um período" do menu de ano: oculta o ano de origem e adiciona a coluna do período
   * escolhido. Reaproveita `onPeriodAdd` (guarda de idempotência + reload) para a parte de adicionar — só a
   * parte de ocultar é nova aqui. Ocultar não precisa de reload por si só (ver `hideYear`), mas o reload do
   * `onPeriodAdd` já recarrega a tela inteira, então o resultado final reflete as duas mudanças juntas.
   */
  onYearReplace(table: DfpPeriodTable, event: { year: number; column: DfpColumn }): void {
    this.hideYear(table, event.year);
    this.onPeriodAdd(table, event.column);
  }

  /**
   * "Remover esta coluna" do menu de ano: só oculta — não precisa de reload (ver `hideYear`). Não recarrega
   * a lista de documentos de origem do backend (ele não conhece `hiddenYears`), mas reprocessa localmente a
   * partir de `rawSourceDocuments` para o painel não continuar afirmando que um documento alimenta um ano que
   * acabou de sumir da tela. Reprocessa a partir da cópia CRUA, não de `sourceDocuments` (ver comentário do
   * campo) — senão uma ocultação anterior nunca poderia ser desfeita por `onYearRestore`.
   */
  onYearRemove(table: DfpPeriodTable, year: number): void {
    this.hideYear(table, year);
    this.sourceDocuments = this.applyHiddenYears(this.rawSourceDocuments);
  }

  /**
   * Restaura um ano ocultado — emitido pela seção "Anos ocultos" tanto do menu de ano quanto do "+"
   * (`app-period-column-add`), que convergem no mesmo evento. Também não precisa de reload: os dados daquele
   * ano já estão em `tableDataFor(table)`, só o filtro de exibição muda. Mesmo raciocínio de `onYearRemove`
   * para `sourceDocuments`: reprocessa a partir de `rawSourceDocuments` para o documento poder voltar a
   * aparecer em "em uso".
   */
  onYearRestore(table: DfpPeriodTable, year: number): void {
    const current = this.hiddenYearsFor(table);
    if (!current.includes(year)) return;

    this.setHiddenYears(
      table,
      current.filter(hidden => hidden !== year)
    );
    this.sourceDocuments = this.applyHiddenYears(this.rawSourceDocuments);
    this.cdr.markForCheck();
  }

  /**
   * Oculta um ano na tabela pedida, sem duplicar e sempre ordenado crescente — a ordem importa porque tanto
   * o menu de ano quanto o "+" listam "Anos ocultos" na mesma ordem das colunas. Não recarrega os dados: o
   * ano já está em `tableDataFor(table)`, ocultar é só um filtro de exibição (`TableAbstractComponent.
   * buildVisibleColumns`), então basta `markForCheck` para propagar a nova referência do array ao `@Input`.
   */
  private hideYear(table: DfpPeriodTable, year: number): void {
    const current = this.hiddenYearsFor(table);
    if (current.includes(year)) return;

    this.setHiddenYears(
      table,
      [...current, year].sort((a, b) => a - b)
    );
    this.cdr.markForCheck();
  }

  private hiddenYearsFor(table: DfpPeriodTable): number[] {
    if (table === 'income') return this.dreHiddenYears;
    if (table === 'balance') return this.balanceHiddenYears;
    return this.highlightsHiddenYears;
  }

  private setHiddenYears(table: DfpPeriodTable, years: number[]): void {
    if (table === 'income') this.dreHiddenYears = years;
    else if (table === 'balance') this.balanceHiddenYears = years;
    else this.highlightsHiddenYears = years;
  }

  /**
   * Recarrega os dados após troca de coluna (`onPeriodAdd`/`onPeriodRemove`). O método de busca é
   * `loadDFPHistoricalData(params)` — privado e com parâmetro obrigatório —, então os params precisam ser
   * remontados aqui a partir da empresa selecionada em vez de reaproveitados de algum estado salvo.
   */
  private reloadDFPHistorical(): void {
    if (!this.selectedCompanyId) return;
    const params = { companyId: this.selectedCompanyId } as dfpHistoricalRequest;
    this.loadDFPHistoricalData(params);
  }

  /**
   * Remove um documento de origem pedido pelo painel (card #2932), após confirmação explícita.
   *
   * A confirmação vive aqui, e não no painel, pelo mesmo motivo do carregamento: o painel é apresentacional
   * e não conhece empresa. O texto diz **o que sai da tela** (demonstrativo + anos da cobertura) porque o
   * usuário está olhando uma tabela de números, não uma lista de arquivos — "remover documento" sozinho não
   * deixa claro que uma coluna inteira desaparece junto.
   *
   * Recarrega a DFP inteira no sucesso, não só o painel: remover derruba as linhas que alimentavam a coluna,
   * então DRE/Balanço/Faturamento e os indicadores derivados mudam na mesma operação.
   */
  onRemoveSourceDocument(document: dfpSourceDocumentResponse): void {
    if (!this.selectedCompanyId || !document.canRemove) return;

    const description = describeSourceRemoval(document);

    this.confirmationService
      .confirm(`Remover "${document.fileName}"?`, description, 'danger', 'Cancelar', 'Remover')
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmado => {
        if (!confirmado) return;
        this.removeSourceDocument(document);
      });
  }

  /**
   * Chamada de remoção propriamente dita. Erro de negócio do backend (documento sintético, perfil sem
   * permissão) chega em `errors[].message` e é essa mensagem que vai ao toast — ela é o que diz ao usuário
   * o que fazer; texto genérico fica reservado para falha técnica.
   */
  private removeSourceDocument(document: dfpSourceDocumentResponse): void {
    this.setIsLoadingLocal(true);

    this.dfpService
      .removeSourceDocument(this.selectedCompanyId!, document.kind, document.id)
      // `finalize` e não um `setIsLoadingLocal(false)` só no sucesso: `utilsService.setIsLoading` é um
      // CONTADOR de referência (`utils.service.ts`), e só despacha `false` quando ele zera. Sem fechar o
      // `true` deste método ANTES de `reloadDFPHistorical()` — que abre e fecha o seu próprio — o contador
      // termina em 1 e o `<app-loading>` global fica na tela até um F5. É a mesma armadilha já registrada
      // no comentário do `handleLoadPage` deste arquivo.
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.setIsLoadingLocal(false))
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(
            'Documento removido',
            `"${document.fileName}" e os valores que ele alimentava saíram da Análise Econômica.`
          );
          this.reloadDFPHistorical();
        },
        // Sem `setIsLoadingLocal(false)` aqui: quem fecha é o `finalize` acima, nos dois desfechos.
        // Chamar nos dois lugares decrementaria duas vezes e deixaria o contador em -1 — e aí o PRÓXIMO
        // loading não apareceria, porque o dispatch de `true` só acontece quando o contador está em 0.
        error: (error: unknown) => {
          const errors = (error as { errors?: { message: string }[] })?.errors ?? [];
          this.toast.showToastError(
            'Não foi possível remover o documento',
            errors[0]?.message ??
              'Houve uma falha ao remover o documento. Tente novamente em alguns instantes.'
          );
        },
      });
  }

  private periodColumnsFor(table: DfpPeriodTable): DfpColumn[] {
    if (table === 'income') return this.drePeriodColumns;
    if (table === 'balance') return this.balancePeriodColumns;
    return this.highlightsPeriodColumns;
  }

  /** Linhas de dados da tabela pedida — fonte de verdade das colunas que realmente existem na tela. */
  private tableDataFor(table: DfpPeriodTable): DfpPeriodAwareRow[] {
    if (table === 'income') return this.incomeStatementTableData;
    if (table === 'balance') return this.balanceSheetTableData;
    return this.highlightsTableData;
  }

  private setPeriodColumns(table: DfpPeriodTable, columns: DfpColumn[]): void {
    if (table === 'income') this.drePeriodColumns = columns;
    else if (table === 'balance') this.balancePeriodColumns = columns;
    else this.highlightsPeriodColumns = columns;
  }

  private processDFPHistoricalData(data: {
    incomeStatement: dfpHistoricalIncomeStatementResponse[] | null;
    balanceSheet: dfpHistoricalBalanceSheetResponse[] | null;
    highlights: dfpHistoricalHighlightsResponse[] | null;
    revenueStatement: dfpHistoricalRevenueStatementResponse[] | null;
  }): void {
    this.incomeStatementTableData = data.incomeStatement || [];
    this.balanceSheetTableData = data.balanceSheet || [];
    this.highlightsTableData = data.highlights || [];
    this.revenueStatementTableData = data.revenueStatement || [];
    this.hasTableData = true;

    // As QUATRO vazias, não só DRE e Balanço: empresa que só tem Faturamento validado tem sim o que
    // mostrar (e cai justamente no `error` do `/dfp/status`, que exige anexo de Balanço e DRE) — chamá-la
    // de "sem dados" esconderia a tabela de faturamento que já aparecia.
    this.hasNoValidatedData =
      !this.incomeStatementTableData.length &&
      !this.balanceSheetTableData.length &&
      !this.highlightsTableData.length &&
      !this.revenueStatementTableData.length;
  }

  handleCheckedChange($event: Checkbox) {
    this.showAnalysisColumns[$event.value] = $event.isChecked ?? false;
  }

  /**
   * Troca a escala de exibição dos valores. Sem recarga: os dados já estão em memória e só a formatação das
   * tabelas muda (ver `TableAbstractComponent.formatValue`).
   *
   * Valor desconhecido cai em `original`, e não num estado intermediário: o `app-select` entrega o `value` da
   * opção como string, e é melhor voltar ao padrão da tela do que exibir número em escala que ninguém pediu.
   */
  onValueScaleChange(scale: string | null): void {
    this.valueScale = scale === 'thousands' ? 'thousands' : 'original';
  }

  /**
   * Falha de carga: preenche `loadFailure` e a tela mostra só `app-dfp-load-error` (o template esconde filtros e
   * tabelas enquanto ele existe, inclusive numa recarga por coluna de período). Sem toast (um erro, um lugar).
   * Mensagem técnica do backend (inglês, exceção) cai no texto padrão do cartão.
   */
  private handleError(error: unknown): void {
    this.loadFailure = { message: userFacingBackendMessage(error) };
    // Um erro, um lugar: o cartão `app-dfp-load-error` (role=alert) já diz o que houve. Sem toast.
    this.setIsLoadingLocal(false);
  }

  /** "Tentar de novo" do estado de erro de carga. */
  retryLoad(): void {
    this.clearPageState();
    this.handleLoadPage();
  }

  getSelectedCompanyName(): string {
    const selectedCompany = this.companiesOptions.find(
      option => option.value === this.selectedCompanyId
    );
    return selectedCompany?.label || '';
  }
}
